import { useState, useEffect } from 'react';
import { apiClient } from '../lib/api-client';
import { langName } from '../lib/lang-names';

interface LanguageCoverage {
  total: number;
  published: number;
  levels: Record<string, number>;
}

interface CoverageData {
  total_dialogs: number;
  total_published: number;
  languages_with_content: number;
  by_language: Record<string, LanguageCoverage>;
  detail: Array<{
    language_code: string;
    level: string;
    count: number;
    published: number;
    workflow_types: string[];
  }>;
}

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function cellColor(count: number): string {
  if (count === 0) return 'bg-zinc-800/50';
  if (count < 3) return 'bg-amber-900/40 text-amber-300';
  if (count < 10) return 'bg-teal-900/40 text-teal-300';
  return 'bg-teal-700/50 text-teal-200 font-medium';
}

type EmailCoverage = Record<string, Record<string, number>>;

export default function Coverage() {
  const [data, setData] = useState<CoverageData | null>(null);
  const [emailCoverage, setEmailCoverage] = useState<EmailCoverage>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      apiClient<CoverageData>('/api/coverage'),
      apiClient<{ coverage: EmailCoverage }>('/api/admin/emails/coverage').catch(() => ({ coverage: {} })),
    ])
      .then(([cov, emails]) => {
        setData(cov);
        setEmailCoverage(emails.coverage);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-6 text-zinc-500">Loading coverage data...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-400">Error: {error}</div>;
  }

  if (!data) return null;

  const languages = Object.entries(data.by_language)
    .sort(([, a], [, b]) => b.total - a.total)
    .filter(([code]) => !search || code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl md:text-2xl font-semibold text-white">Coverage Dashboard</h1>
        <div className="text-sm text-zinc-500">
          Auto-refreshes with each pipeline run
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <SummaryCard label="Total Dialogs" value={data.total_dialogs} />
        <SummaryCard label="Published" value={data.total_published} />
        <SummaryCard label="Languages" value={data.languages_with_content} />
        <SummaryCard
          label="Publish Rate"
          value={data.total_dialogs > 0 ? `${Math.round((data.total_published / data.total_dialogs) * 100)}%` : '0%'}
        />
      </div>

      {/* Search + legend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Filter languages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-64 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-teal-500"
        />
        {Object.keys(emailCoverage).length > 0 && (
          <span className="text-xs text-zinc-500">
            <span className="text-pink-400 font-bold">&#10003;</span> = nurture emails generated
          </span>
        )}
      </div>

      {/* Coverage grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 sticky left-0 bg-bg-primary">
                Language
              </th>
              <th className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">
                Total
              </th>
              <th className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">
                Published
              </th>
              {LEVELS.map((lvl) => (
                <th key={lvl} className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">
                  {lvl}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {languages.map(([code, lang]) => (
              <tr key={code} className="hover:bg-zinc-800/50">
                <td className="px-3 py-1.5 text-white font-mono text-xs border-b border-zinc-800 sticky left-0 bg-bg-primary" title={langName(code)}>
                  {code}
                </td>
                <td className="px-3 py-1.5 text-center border-b border-zinc-800 text-zinc-300">
                  {lang.total}
                </td>
                <td className="px-3 py-1.5 text-center border-b border-zinc-800 text-teal-400">
                  {lang.published}
                </td>
                {LEVELS.map((lvl) => {
                  const count = lang.levels[lvl] ?? 0;
                  const emailCount = emailCoverage[code]?.[lvl] ?? 0;
                  return (
                    <td key={lvl} className={`px-3 py-1.5 text-center border-b border-zinc-800 ${cellColor(count)}`} title={emailCount ? `${emailCount} emails` : undefined}>
                      {count || '-'}
                      {emailCount > 0 && <span className="text-pink-400 ml-0.5 text-[10px] font-bold">&#10003;</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {languages.length === 0 && (
        <p className="text-zinc-500 text-center py-8">No coverage data yet. The pipeline will populate this as dialogs are generated.</p>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
      <div className="text-zinc-400 text-xs uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold text-white mt-1">{value}</div>
    </div>
  );
}
